import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '@kapture/client-core';

const Credentials = z.object({
  email: z.string().min(1, 'Email is required').email('That does not look like an email'),
  password: z.string().min(1, 'Password is required'),
});
type Credentials = z.infer<typeof Credentials>;

export const Route = createFileRoute('/login')({
  component: LoginPage,
  staticData: { mobile: true },
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
});

function LoginPage() {
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: '/login' });
  const login = useAuthStore((s) => s.login);
  const status = useAuthStore((s) => s.status);
  const serverError = useAuthStore((s) => s.error);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Credentials>({ resolver: zodResolver(Credentials) });

  const submitting = status === 'authenticating';

  async function onSubmit(values: Credentials) {
    try {
      await login(values.email, values.password);
      navigate({ to: redirect ?? '/' });
    } catch {
      // The store already holds the message; nothing to do here but stay put.
    }
  }

  return (
    <section className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
      >
        <div className="bg-ink text-brand mb-8 grid h-12 w-12 place-items-center rounded-xl">
          <span className="font-mono text-[11px] font-bold tracking-tight">KAP</span>
        </div>
        <h1 className="font-sans text-2xl font-bold tracking-[-0.02em]">Sign in</h1>
        <p className="text-text-dim mt-2 text-sm">
          Your work stays on this device until you're back on a network.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-4" noValidate>
          <div>
            <label
              htmlFor="email"
              className="text-text-dim font-mono text-[11px] font-semibold uppercase tracking-wider"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              aria-invalid={errors.email ? 'true' : undefined}
              className="border-stroke-field bg-surface text-ink focus:border-brand-deep mt-1.5 h-11 w-full rounded-lg border px-3 text-sm outline-none"
              {...register('email')}
            />
            {errors.email && (
              <p role="alert" className="text-danger mt-1.5 text-xs">
                {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="password"
              className="text-text-dim font-mono text-[11px] font-semibold uppercase tracking-wider"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              aria-invalid={errors.password ? 'true' : undefined}
              className="border-stroke-field bg-surface text-ink focus:border-brand-deep mt-1.5 h-11 w-full rounded-lg border px-3 text-sm outline-none"
              {...register('password')}
            />
            {errors.password && (
              <p role="alert" className="text-danger mt-1.5 text-xs">
                {errors.password.message}
              </p>
            )}
          </div>

          {serverError && (
            <p
              role="alert"
              className="border-danger/30 bg-danger/5 text-danger rounded-lg border px-3 py-2 text-xs"
            >
              {serverError}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="bg-brand text-brand-ink inline-flex h-11 w-full items-center justify-center rounded-lg px-5 text-sm font-semibold transition-transform hover:-translate-y-px disabled:translate-y-0 disabled:opacity-60"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </motion.div>
    </section>
  );
}
